import React, { useState } from "react";
import { Plus, Trash2, Wand2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";

/**
 * Categories management — the editorial taxonomy that drives:
 *   1. Public `/category/<slug>` pages
 *   2. The AI rewriter's allowed `category_slug` choices
 *
 * Adding "Sports" or "Entertainment" here unlocks proper bucketing for sites
 * that ingest non-finance content. The "Re-classify articles" button runs a
 * keyword heuristic that moves existing published/ready articles into the
 * right bucket — useful right after adding a new category.
 */
export default function CategoriesPanel({ site, reload }) {
  const [cats, setCats] = useState(() => [...(site.categories || [])]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(null); // 'add' | 'del:<slug>' | 'dry' | 'apply'
  const [flash, setFlash] = useState(null);
  const [preview, setPreview] = useState(null); // { changed_count, changes[], allowed_categories[] }

  function slugify(s) {
    return (s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  async function add() {
    if (busy) return;
    const name = newName.trim();
    if (!name) return;
    setBusy("add"); setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/categories`, { name });
      setCats(r.data.categories || []);
      setNewName("");
      setFlash({ kind: "ok", msg: `Added "${name}"` });
      await reload?.();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Failed to add category" });
    } finally { setBusy(null); }
  }

  async function remove(slug) {
    if (busy) return;
    if (!window.confirm(`Delete the "${slug}" category?`)) return;
    setBusy(`del:${slug}`); setFlash(null);
    try {
      const r = await api.delete(`/sites/${site.id}/categories/${encodeURIComponent(slug)}`);
      setCats(r.data.categories || []);
      setFlash({ kind: "ok", msg: `Removed "${slug}"` });
      await reload?.();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || `Cannot delete "${slug}"` });
    } finally { setBusy(null); }
  }

  async function reclassify(applyChanges) {
    if (busy) return;
    setBusy(applyChanges ? "apply" : "dry"); setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/articles/reclassify`, { dry_run: !applyChanges });
      setPreview(r.data);
      if (applyChanges) {
        setFlash({ kind: "ok", msg: `Re-categorized ${r.data.changed_count} article(s)` });
        await reload?.();
      } else {
        setFlash({ kind: "ok", msg: `Dry-run: ${r.data.changed_count} article(s) would change. Review below.` });
      }
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Re-classification failed" });
    } finally { setBusy(null); }
  }

  return (
    <div className="border border-zinc-800 rounded-sm p-5 bg-zinc-900/30 space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="font-display text-lg font-bold">Editorial Categories</div>
          <div className="text-xs text-zinc-500 mono mt-1">
            Drive <span className="text-zinc-300">/category/&lt;slug&gt;</span> pages and the AI rewriter&apos;s allowed taxonomy.
          </div>
        </div>
        <div className="text-xs text-zinc-500 mono">{cats.length} {cats.length === 1 ? "category" : "categories"}</div>
      </div>

      {/* Existing list */}
      <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800 bg-zinc-950/40">
        {cats.length === 0 && (
          <div className="px-4 py-3 text-xs text-zinc-500 mono">No categories yet. Add one below.</div>
        )}
        {cats.map((c) => (
          <div
            key={c.slug}
            data-testid={SITEDETAIL.categoryRow(c.slug)}
            className="flex items-center justify-between gap-3 px-4 py-2.5"
          >
            <div className="min-w-0">
              <div className="text-sm text-zinc-200 truncate">{c.name}</div>
              <div className="text-[11px] text-zinc-500 mono">/category/{c.slug}</div>
            </div>
            <button
              data-testid={SITEDETAIL.categoryDelete(c.slug)}
              onClick={() => remove(c.slug)}
              disabled={!!busy}
              className="text-xs text-zinc-500 hover:text-rose-400 disabled:opacity-40 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm hover:bg-rose-500/5"
              aria-label={`Delete ${c.name}`}
              title={`Delete ${c.name}`}
            >
              {busy === `del:${c.slug}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        ))}
      </div>

      {/* Add new */}
      <div className="flex items-center gap-2">
        <input
          data-testid={SITEDETAIL.categoryNewName}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="New category name (e.g. Sports, Entertainment)"
          className="admin-input flex-1 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
          disabled={!!busy}
        />
        <div className="text-[11px] text-zinc-600 mono whitespace-nowrap">slug: /category/{slugify(newName) || "…"}</div>
        <button
          data-testid={SITEDETAIL.categoryAdd}
          onClick={add}
          disabled={!!busy || !newName.trim()}
          className="bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 px-4 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-1.5"
        >
          {busy === "add" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add
        </button>
      </div>

      {/* Re-classify */}
      <div className="pt-3 mt-3 border-t border-zinc-800">
        <div className="font-display text-sm font-bold mb-1">Re-classify existing articles</div>
        <div className="text-xs text-zinc-500 mb-3">
          Scans every ready / published / archived article on this site and moves it into the strongest-matching category
          (using keyword heuristics like NBA → Sports, Hollywood → Entertainment). Only moves into categories you have here.
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={SITEDETAIL.reclassifyDryRun}
            onClick={() => reclassify(false)}
            disabled={!!busy}
            className="border border-zinc-700 hover:border-zinc-500 text-zinc-200 px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            {busy === "dry" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Dry-run preview
          </button>
          <button
            data-testid={SITEDETAIL.reclassifyApply}
            onClick={() => reclassify(true)}
            disabled={!!busy || !preview || preview.changed_count === 0}
            className="bg-indigo-500/20 border border-indigo-500/40 hover:bg-indigo-500/30 text-indigo-200 px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
            title={preview && preview.changed_count > 0 ? `Apply ${preview.changed_count} change(s)` : "Run a dry-run first"}
          >
            {busy === "apply" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Apply changes
          </button>
        </div>

        {preview && (
          <div className="mt-3 border border-zinc-800 rounded-sm overflow-hidden">
            <div className="px-3 py-2 bg-zinc-900/60 text-[11px] text-zinc-400 mono border-b border-zinc-800">
              {preview.dry_run ? "DRY-RUN — " : "APPLIED — "}
              {preview.changed_count} change{preview.changed_count === 1 ? "" : "s"}
            </div>
            {preview.changes?.length > 0 ? (
              <div className="max-h-72 overflow-auto divide-y divide-zinc-800">
                {preview.changes.map((c) => (
                  <div key={c.article_id} className="px-3 py-2 text-xs">
                    <div className="text-zinc-300 truncate">{c.title}</div>
                    <div className="mt-0.5 mono text-[10px] text-zinc-500">
                      <span className="text-rose-400">{c.from}</span>
                      <span className="mx-2">→</span>
                      <span className="text-emerald-400">{c.to}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3 py-3 text-xs text-zinc-500 mono">No changes — every article already in the strongest-matching bucket.</div>
            )}
          </div>
        )}
      </div>

      {flash && (
        <div
          data-testid="sitedetail-categories-flash"
          className={`text-xs mono ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`}
        >
          {flash.kind === "ok" ? "✓ " : "✕ "}{flash.msg}
        </div>
      )}
    </div>
  );
}
